/**
 * 定时列表页 —— 从 JobList 顶部 Tab 提为一级路由 (/schedule/list)。
 *
 * docs/platform-refactor-v1.md §1.1：计划任务从 JobList Tab 提为一级
 *
 * 功能与旧 Tab 完全对等：
 *   - 列表 + 状态筛选 + 分页
 *   - 立即触发 / 取消 / 删除 / 查看触发日志抽屉
 *
 * 数据/接口均沿用现有 apis/simulation.ts，无后端改动。
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  App, Button, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileTextOutlined } from '@ant-design/icons';
import { MiddleEllipsis } from '@components/ResizableTable';
import ScheduleLogDrawer from '@components/ScheduleLogDrawer';
import {
  cancelSchedule,
  deleteSchedule,
  listSchedules,
  manualFireSchedule,
  type ScheduleItem,
  type ScheduleStatus,
} from '@apis/simulation';

const { Title, Paragraph } = Typography;

export default function ScheduleListPage() {
  const { message } = App.useApp();
  const [list, setList] = useState<ScheduleItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<ScheduleStatus | undefined>(undefined);
  const [logDrawer, setLogDrawer] = useState<{ open: boolean; id: number | null; name?: string | null }>({
    open: false,
    id: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSchedules({ page, pageSize, status: statusFilter });
      setList(res.items);
      setTotal(res.total);
    } catch (err) {
      console.error('加载定时列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async (id: number) => {
    try {
      await cancelSchedule(id);
      message.success('已取消定时');
      load();
    } catch (err) {
      console.error('取消定时失败:', err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSchedule(id);
      message.success('已删除定时记录');
      load();
    } catch (err) {
      console.error('删除定时失败:', err);
    }
  };

  const handleManualFire = async (record: ScheduleItem) => {
    try {
      const log = await manualFireSchedule(record.id);
      if (log.trigger_status === 'success') {
        message.success(`已触发，job_id=${log.job_id ?? '-'}`);
      } else {
        message.error(`触发失败：${log.error_msg ?? log.error_type ?? '未知错误'}`);
      }
      load();
    } catch (err) {
      console.error('手动触发失败:', err);
    }
  };

  const columns: ColumnsType<ScheduleItem> = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record) => (
        <Tooltip title={text}>
          <span
            style={{ color: '#5e6ad2', cursor: 'pointer' }}
            onClick={() => setLogDrawer({ open: true, id: record.id, name: record.name })}
          >
            <MiddleEllipsis text={text} />
          </span>
        </Tooltip>
      ),
    },
    {
      title: '触发对象',
      dataIndex: 'job_endpoint',
      key: 'job_endpoint',
      width: 110,
      align: 'center',
      render: (ep: string) => (ep === 'bazel_create' ? <Tag color="purple">bazel 编译</Tag> : <Tag color="blue">通用创建</Tag>),
    },
    {
      title: '触发方式',
      dataIndex: 'kind',
      key: 'kind',
      width: 100,
      align: 'center',
      render: (kind: string) => {
        if (kind === 'once_at') return <Tag>定时</Tag>;
        if (kind === 'once_after') return <Tag color="cyan">延时</Tag>;
        if (kind === 'cron') return <Tag color="geekblue">循环</Tag>;
        return <Tag>{kind}</Tag>;
      },
    },
    {
      title: '触发时间',
      key: 'fire_info',
      width: 240,
      render: (_: unknown, record) => {
        if (record.kind === 'cron') {
          return (
            <Tooltip title={`时区：${record.cron_tz || 'Asia/Shanghai'}`}>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{record.cron_expr || '-'}</span>
            </Tooltip>
          );
        }
        if (!record.fire_at) return '-';
        try {
          return (
            <Tooltip title={record.fire_at}>
              <span>{new Date(record.fire_at).toLocaleString('zh-CN')}</span>
            </Tooltip>
          );
        } catch {
          return record.fire_at;
        }
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: string) => {
        const map: Record<string, { label: string; color: string }> = {
          active: { label: '待触发', color: 'processing' },
          done: { label: '已完成', color: 'green' },
          failed: { label: '失败', color: 'red' },
          cancelled: { label: '已取消', color: 'default' },
        };
        const m = map[status] ?? { label: status, color: 'default' };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: '上次触发结果',
      key: 'last_result',
      width: 120,
      align: 'center',
      render: (_: unknown, record) => {
        if (!record.last_fired_at) return <span style={{ color: '#c4c6cc' }}>-</span>;
        if (record.last_error) return <Tag color="red">失败</Tag>;
        if (record.last_job_id) return <Tag color="green">成功</Tag>;
        return <span style={{ color: '#c4c6cc' }}>-</span>;
      },
    },
    {
      title: '上次 Job ID',
      dataIndex: 'last_job_id',
      key: 'last_job_id',
      width: 110,
      align: 'center',
      render: (jobId: number | null) => {
        if (jobId == null) return <span style={{ color: '#c4c6cc' }}>-</span>;
        return (
          <Link to={`/job-tasks/${jobId}`} style={{ color: '#5e6ad2', cursor: 'pointer' }}>
            {jobId}
          </Link>
        );
      },
    },
    {
      title: '创建人',
      dataIndex: 'owner',
      key: 'owner',
      width: 130,
      render: (text: string) => (text ? <MiddleEllipsis text={text} /> : '-'),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (text: string) => {
        if (!text) return '-';
        try {
          return <MiddleEllipsis text={new Date(text).toLocaleString('zh-CN')} />;
        } catch {
          return text;
        }
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      align: 'center',
      render: (_: unknown, record) => {
        const canCancel = record.status === 'active' && record.can_delete;
        return (
          <Space>
            <Popconfirm
              title="立即触发一次"
              description="将立即创建一个 Job（同步等待结果）；不影响定时 / 延时 / 循环节奏。"
              onConfirm={() => handleManualFire(record)}
              okText="触发"
              cancelText="关闭"
            >
              <Button size="small" type="primary">立即触发</Button>
            </Popconfirm>
            {canCancel ? (
              <Popconfirm
                title="确认取消"
                description="取消后该定时将不再触发。仅 active 状态可取消。"
                onConfirm={() => handleCancel(record.id)}
                okText="确定"
                cancelText="关闭"
              >
                <Button size="small" danger>取消</Button>
              </Popconfirm>
            ) : (
              <Button size="small" danger disabled>取消</Button>
            )}
            {record.can_delete ? (
              <Popconfirm
                title="确认删除"
                description="将物理删除该定时记录及全部触发日志，不可恢复。"
                onConfirm={() => handleDelete(record.id)}
                okText="确定删除"
                okButtonProps={{ danger: true }}
                cancelText="关闭"
              >
                <Button size="small" danger>删除</Button>
              </Popconfirm>
            ) : (
              <Button size="small" danger disabled>删除</Button>
            )}
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => setLogDrawer({ open: true, id: record.id, name: record.name })}
            >
              查看日志
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>定时列表</Title>
        <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
          定时 / 延时 / 循环触发的 Job 排程。创建入口仍在「创建 Job」表单的高级选项 · 开启定时。
        </Paragraph>
      </div>

      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#5e6ad2' }}>状态：</span>
        <Select
          allowClear
          placeholder="按状态筛选"
          style={{ width: 150 }}
          value={statusFilter}
          onChange={(v: ScheduleStatus | undefined) => {
            setStatusFilter(v);
            setPage(1);
          }}
          options={[
            { label: '待触发', value: 'active' },
            { label: '已完成', value: 'done' },
            { label: '失败', value: 'failed' },
            { label: '已取消', value: 'cancelled' },
          ]}
        />
        <Button onClick={() => load()}>刷新</Button>
      </div>

      <Table
        rowKey="id"
        dataSource={list}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `总共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      <ScheduleLogDrawer
        open={logDrawer.open}
        scheduleId={logDrawer.id}
        scheduleName={logDrawer.name}
        onClose={() => setLogDrawer({ open: false, id: null })}
      />
    </div>
  );
}
